/**
 * TanStack Query hooks.
 *
 * Refetch intervals are tuned to the measured 180s reporting cadence. Polling faster than
 * the data changes burns requests for nothing; polling much slower makes an operator
 * distrust the screen. 60s gives roughly three chances to catch each new payload without
 * hammering the API.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
  type UseQueryOptions,
  type UseQueryResult,
} from '@tanstack/react-query';
import { useState } from 'react';
import { pollInterval } from './backoff';
import { api, ApiError } from './client';
import type {
  AlarmLifecycleAction,
  AlarmTriage,
  SnoozeHours,
  Aggregation,
  AlarmListResponse,
  AlarmSummaryResponse,
  RangeBucket,
  ComplianceHistoryResponse,
  ExposureResponse,
  IngestQualityResponse,
  HealthResponse,
  LatestResponse,
  MetaResponse,
  RangeResponse,
  SeriesListResponse,
  SeriesRow,
} from './types';

/** Matches the publish cadence measured off the live broker. */
export const REPORTING_CADENCE_MS = 180_000;
const LIVE_REFETCH_MS = 60_000;

export const keys = {
  meta: ['meta'] as const,
  sites: (filters?: Record<string, unknown>) => ['sites', filters ?? {}] as const,
  health: (siteId?: string) => ['telemetry', 'health', siteId ?? 'all'] as const,
  series: (filters: Record<string, unknown>) => ['telemetry', 'series', filters] as const,
  latest: (filters: Record<string, unknown>) => ['telemetry', 'latest', filters] as const,
  range: (filters: Record<string, unknown>) => ['telemetry', 'range', filters] as const,
  alarms: (filters: Record<string, unknown>) => ['alarms', filters] as const,
  alarmSummary: (siteId?: string) => ['alarms', 'summary', siteId ?? 'all'] as const,
  alarmTriage: (id: string) => ['alarms', 'triage', id] as const,
  alarmTiming: (siteId: string | undefined, range: string) => ['alarms', 'timing', siteId ?? 'all', range] as const,
  exposure: ['sites', 'exposure'] as const,
  complianceHistory: (siteId: string) => ['sites', siteId, 'compliance', 'history'] as const,
  ingestQuality: (siteId?: string, days?: number) =>
    ['telemetry', 'ingest-quality', siteId ?? 'all', days ?? 7] as const,
};

/**
 * A 4xx means the request itself is wrong, so retrying cannot help — it just delays the
 * error the user needs to see. 5xx and 429 are worth one retry.
 */
function retryPolicy(count: number, error: unknown): boolean {
  if (error instanceof ApiError && error.isClientFault) return false;
  return count < 2;
}

const base = { retry: retryPolicy } satisfies Partial<UseQueryOptions>;

export function useMeta() {
  return useQuery({
    ...base,
    queryKey: keys.meta,
    queryFn: () => api.get<MetaResponse>('/meta'),
    // Immutable per deployment; the engine digest only changes on deploy.
    staleTime: Infinity,
  });
}

/*
 * `useSites` REMOVED — it lives in `lib/api/sites.ts`.
 *
 * This one took a `filters` argument that all three of its callers left empty, and passed
 * no `limit`, so it silently took the server's default of 50 while the other hook asked for
 * 200. Two cache keys, two page sizes, one question: above fifty sites the gateway-assign
 * and feed-health pickers would have shown a shorter fleet than the header selector, with
 * nothing on screen to say so.
 *
 * If per-field filtering is ever wanted, add it to the surviving hook rather than reviving
 * this one.
 */

export function useSiteHealth(siteId?: string) {
  return useQuery({
    ...base,
    queryKey: keys.health(siteId),
    queryFn: () => api.get<HealthResponse>('/telemetry/health', siteId ? { siteId } : undefined),
    refetchInterval: pollInterval(LIVE_REFETCH_MS),
    staleTime: 30_000,
  });
}

/**
 * Fleet exposure by compliance domain.
 *
 * Fleet-wide by design — there is no `siteId` variant. The question this answers is
 * "which domain is worst ACROSS the estate", and a single-site version of it is the
 * Compliance matrix, which already exists and says more. Adding a site filter here would
 * put two screens on the same fact with different resolutions.
 *
 * Re-evaluated on the server from the live site rows, so it follows a parameter edit on
 * the next fetch rather than on the next evaluation snapshot.
 */
/**
 * A site's score history and what moved it. Site-scoped only — there is no fleet variant,
 * because "why did it move" is a question about one plant's parameters and one plant's
 * audit trail, and averaging that across sites answers nobody.
 */
export function useComplianceHistory(siteId: string | undefined) {
  return useQuery({
    ...base,
    queryKey: keys.complianceHistory(siteId ?? 'none'),
    queryFn: () => api.get<ComplianceHistoryResponse>(`/sites/${siteId}/compliance/history`),
    enabled: Boolean(siteId),
    staleTime: 60_000,
  });
}

export function useFleetExposure() {
  return useQuery({
    ...base,
    queryKey: keys.exposure,
    queryFn: () => api.get<ExposureResponse>('/sites/exposure'),
    staleTime: 60_000,
  });
}

/**
 * Ingest yield and data quality. Slow-moving by nature — these are multi-day totals, so a
 * 5-minute stale time rather than the live cadence the health hook uses.
 */
export function useIngestQuality(siteId?: string, days?: number) {
  return useQuery({
    ...base,
    queryKey: keys.ingestQuality(siteId, days),
    queryFn: () =>
      api.get<IngestQualityResponse>('/telemetry/ingest-quality', {
        ...(siteId ? { siteId } : {}),
        ...(days ? { days: String(days) } : {}),
      }),
    staleTime: 300_000,
  });
}

/*
 * NEITHER OF THE NEXT TWO CARRIES A `refetchInterval` ANY MORE. `useAlarmStream` in
 * `events.ts` invalidates the `alarms` and `alarm-summary` key prefixes on every pushed
 * event AND on every reconnect, and that invalidate is by prefix — it reaches these two
 * plus AlarmsView's paginated query and every other filtered call site (site pages, Overview
 * charts, the Digital Twin, device detail) in one call. A fixed poll on top of that was pure
 * waste. If the stream is down, `enabled: false` there falls back to this hook's own
 * `staleTime`/mount behaviour rather than going stale forever — the push makes this fast,
 * it was never the only thing keeping it correct.
 */
export function useAlarmSummary(siteId?: string) {
  return useQuery({
    ...base,
    queryKey: keys.alarmSummary(siteId),
    queryFn: () => api.get<AlarmSummaryResponse>('/alarms/summary', siteId ? { siteId } : undefined),
  });
}

export function useAlarms(filters: {
  siteId?: string;
  state?: 'open' | 'closed' | 'all';
  severity?: string;
  limit?: number;
} = {}) {
  return useQuery({
    ...base,
    queryKey: keys.alarms(filters),
    queryFn: () => api.get<AlarmListResponse>('/alarms', filters),
  });
}

/** One severity's detect/ack/work stage timing — see `Backend/lumen-be`'s
 *  `modules/alarms/analytics.ts` for why this is the one Analytics chart that reads a
 *  dedicated endpoint rather than being derived here from `/alarms` alone. */
export interface AlarmStageStat {
  medianMs: number;
  p90Ms: number;
  n: number;
}
export interface AlarmSeverityTiming {
  severity: 'critical' | 'major' | 'minor' | 'unknown';
  detect: AlarmStageStat | null;
  ack: AlarmStageStat | null;
  work: AlarmStageStat | null;
}

export function useAlarmTiming(siteId: string | undefined, range: '7d' | '30d' | '90d') {
  return useQuery({
    ...base,
    queryKey: keys.alarmTiming(siteId, range),
    queryFn: () =>
      api.get<{ data: AlarmSeverityTiming[] }>('/alarms/timing', { ...(siteId ? { siteId } : {}), range }),
  });
}

/**
 * THE SITE'S LIVE CATALOGUE, ASKED FOR AS SOON AS THE SITE IS KNOWN (2026-10-08).
 *
 * Every Overview card that charts a reading first asks `/telemetry/series` which series the site
 * has, and only then asks `/telemetry/range` for their data — two round trips in a row before a
 * line can draw, on every cold load. The site context calls this the moment a site is selected
 * and validated, so by the time the Overview's chunk has loaded and its cards mount, the first
 * trip is already answered from the cache. The filter is byte for byte the one the Overview's
 * cards pass (`{ siteId, liveOnly: true, limit: 500 }`), so it is the same cache entry, not a
 * second copy of the list.
 */
export function prefetchLiveSeriesCatalog(qc: QueryClient, siteId: string): void {
  const filters = { siteId, liveOnly: true, limit: 500 };
  void qc.prefetchQuery({
    ...base,
    queryKey: keys.series(filters),
    queryFn: () => api.get<SeriesListResponse>('/telemetry/series', filters),
    staleTime: 5 * 60_000,
  });
}

export function useSeriesCatalog(filters: {
  siteId?: string;
  deviceId?: number;
  liveOnly?: boolean;
  q?: string;
  limit?: number;
} = {}, enabled = true) {
  return useQuery({
    ...base,
    queryKey: keys.series(filters),
    queryFn: () => api.get<SeriesListResponse>('/telemetry/series', filters),
    // The catalog only changes when new hardware appears.
    staleTime: 5 * 60_000,
    enabled,
  });
}

export function useLatest(
  filters: { siteId?: string; deviceId?: number; metrics?: string; limit?: number } = {},
  enabled = true,
) {
  return useQuery({
    ...base,
    queryKey: keys.latest(filters),
    queryFn: () => api.get<LatestResponse>('/telemetry/latest', filters),
    refetchInterval: pollInterval(LIVE_REFETCH_MS),
    enabled,
  });
}

export function useRange(
  params: {
    seriesIds: number[];
    from: string;
    to: string;
    bucket?: string;
    agg?: string;
  },
  enabled = true,
) {
  const query = {
    seriesIds: params.seriesIds.join(','),
    from: params.from,
    to: params.to,
    ...(params.bucket ? { bucket: params.bucket } : {}),
    ...(params.agg ? { agg: params.agg } : {}),
  };
  return useQuery({
    ...base,
    queryKey: keys.range(query),
    queryFn: () => api.get<RangeResponse>('/telemetry/range', query),
    enabled: enabled && params.seriesIds.length > 0,
    staleTime: 60_000,
    // Keep the previous plot and y-domain through a refetch — same reasoning as
    // `useChunkedRange` below, and the one this hook was missing it. Most callers key this
    // on a `to: now`-shaped window that moves every poll or every render (a plant card's own
    // `Date.now()`), so without this every one of those was a NEW query key: `isPending`
    // flips true, the chart drops to its empty/skeleton state, and the page reflows for the
    // beat it takes to refetch — the exact "whole page re-renders" report this hook was named
    // in. `useChunkedRange` already carried the fix for its one call site; this is the same
    // fix for the other ~25.
    placeholderData: (prev) => prev,
  });
}

/* ================================ range cost contract (mirror of the server) */

/**
 * MIRROR of `Backend/lumen-be/src/modules/telemetry/schema.ts`. If that file changes,
 * this changes with it.
 *
 * It lives in the api layer rather than in a screen because THREE callers now need the
 * identical arithmetic and must never disagree: the batch size `useChunkedRange` splits a
 * selection on, the cost readout on the telemetry screen, and the bucket control that
 * decides which intervals are offerable at all. Two independent copies of this arithmetic
 * is exactly how a batch gets sized one series too large and a 553-series selection dies
 * on a 422 that the operator can do nothing about.
 */
export const MAX_BUCKETS = 5_000;
/** Hard per-request ceiling from `rangeQuerySchema` — a Zod `.max()`, not a cost estimate. */
export const MAX_SERIES_PER_QUERY = 50;
export const MAX_RAW_ROWS = 250_000;
/**
 * The interval the SERVER'S ROW ESTIMATE assumes — `estimateCost`'s 180 in the backend's
 * `schema.ts` — and therefore the only cadence the cost mirror below may use.
 *
 * IT IS NOT A SITE'S CADENCE, and it stopped being a fair stand-in for one: JAZZMSC5 publishes
 * every 180 s and Jamshoro every 60 s (verified on the live broker 2026-09-28). Anything that
 * judges a SITE — how stale is stale, whether a bucket is native resolution — reads
 * `useSiteCadence` below. Only the batch arithmetic stays pinned here, because a mirror that
 * diverges from the server's own estimate is how a batch gets sized past the 422.
 */
export const CADENCE_SECONDS = 180;

/**
 * A site's OWN measured publish interval, from `/telemetry/ingest-quality`, falling back to
 * `CADENCE_SECONDS` while it loads or for a site with too few payloads to measure.
 */
export function useSiteCadence(siteId: string | undefined): number {
  const q = useIngestQuality(siteId, 1);
  const row = siteId ? q.data?.data.find((r) => r.siteId === siteId) : undefined;
  const v = row?.cadenceSeconds ?? null;
  return v !== null && v > 0 ? v : CADENCE_SECONDS;
}

export const BUCKET_SECONDS: Record<RangeBucket, number> = {
  '1 minute': 60,
  '3 minutes': 180,
  '15 minutes': 900,
  '1 hour': 3_600,
  '6 hours': 21_600,
  '1 day': 86_400,
  '7 days': 604_800,
  /* Nominal, as the server's is — for counting buckets, never for placing one. */
  '1 month': 2_592_000,
};

export function bucketsFor(windowSeconds: number, bucket: RangeBucket): number {
  return Math.ceil(windowSeconds / BUCKET_SECONDS[bucket]);
}

/**
 * Seconds one scanned row stands for in the SERVER'S estimate for this bucket — its
 * `rawRowSeconds`: `CADENCE_SECONDS`, except a one-minute bucket, which the server costs at
 * the 60 s it implies. Three times the rows, so a batch sized at 180 s would be refused.
 */
export function rowSecondsFor(bucket?: RangeBucket): number {
  return bucket === '1 minute' ? 60 : CADENCE_SECONDS;
}

/**
 * Raw rows one request will scan.
 *
 * The server computes `ceil((windowSeconds / 180) * seriesCount)`; this ceils the
 * per-series term FIRST, so it can only ever over-estimate. Deliberate: a client mirror
 * that under-estimates by one row hands the operator a 422, and being one row pessimistic
 * costs nothing.
 */
export function rawRowsFor(windowSeconds: number, seriesCount: number, bucket?: RangeBucket): number {
  return Math.ceil(windowSeconds / rowSecondsFor(bucket)) * seriesCount;
}

/**
 * Series per request for this window — i.e. the batch size.
 *
 * THE PROPERTY THAT MATTERS: `estimatedRawRows` does not depend on `bucket`, so coarsening
 * the bucket does nothing for a row breach and the two remedies stay disjoint. Splitting
 * the selection is the ONLY remedy that keeps every series.
 *
 * The `max(1, …)` clamp: a window long enough that even a single series breaches the row
 * budget (>12.5 years at the measured cadence) has no batch size that fits. Sending one
 * series and letting the server refuse it is the honest outcome — silently returning 0
 * would produce an empty chart with no error.
 */
export function maxSeriesPerRequest(windowSeconds: number, bucket?: RangeBucket): number {
  const perSeries = Math.ceil(windowSeconds / rowSecondsFor(bucket));
  return Math.max(1, Math.min(MAX_SERIES_PER_QUERY, Math.floor(MAX_RAW_ROWS / perSeries)));
}

/** True when the metric has ever carried a value — i.e. the site really polls it. */
export function isPolled(row: SeriesRow): boolean {
  return row.everHadValue;
}

/* ==================================================== chunked range fetching */

/**
 * Concurrency for the batch pool.
 *
 * 4 rather than "all of them": a 545-series selection over 30 days is 33 requests, and 33
 * concurrent raw-scan queries against a hypertable with no rollups is the exact stall the
 * cost guard exists to prevent — splitting the work and then firing it all at once would
 * respect the letter of the guard and defeat its purpose. 4 also sits under the browser's
 * 6-per-host HTTP/1.1 limit, so the other calls on the page (health, alarms, catalog) are
 * not queued behind the batch run.
 */
const RANGE_CONCURRENCY = 4;

/** Per-batch attempts. One retry covers a transient blip without re-running 33 requests. */
const BATCH_ATTEMPTS = 2;
const BATCH_RETRY_MS = 400;

export interface ChunkedRangeProgress {
  /** Requests that have settled — resolved or given up on. */
  requestsDone: number;
  requestsTotal: number;
  /** Series whose request has come back. NOT the count that carried data. */
  seriesDone: number;
  seriesTotal: number;
}

export interface ChunkedRangeResult {
  /** The batches merged into one response, ordered to match the requested id order. */
  response: RangeResponse;
  requestsOk: number;
  requestsTotal: number;
  /**
   * Series whose batch failed after retries, so they are absent from `response`.
   *
   * Reported rather than thrown: losing 17 of 545 series to one flaky request should not
   * blank a chart that has 528 good ones on it — but it must not look like 528 was all
   * that was asked for either.
   */
  failedSeriesIds: number[];
  /** Series per request for this window. */
  batchSize: number;
  /**
   * The ids this result ANSWERS, in the order they were requested.
   *
   * Carried on the result because `placeholderData: (prev) => prev` below hands the
   * PREVIOUS selection's response to the new selection's cache slot for the whole duration
   * of a sweep — 11 requests at 24 h, 33 at 30 d. A caller that diffs "which requested ids
   * came back empty" against that placeholder concludes that almost everything is unknown.
   * That is not hypothetical: it shipped, and it rendered a "these series returned nothing
   * — drop them" action over a still-running sweep, which took a 545-series selection back
   * to 1 in one click. `isPlaceholderData` says the same thing, but this makes the
   * correspondence checkable from the DATA rather than from a flag, so the check survives
   * anyone changing the key or the placeholder strategy.
   */
  requestedSeriesIds: number[];
}

export function chunkIds(ids: readonly number[], size: number): number[][] {
  const out: number[][] = [];
  for (let i = 0; i < ids.length; i += size) out.push([...ids.slice(i, i + size)]);
  return out;
}

/**
 * Merge batch responses into one.
 *
 * `cost` is summed, not averaged: the scan the database actually performed is the sum of
 * the batches, and reporting one batch's cost as the query's cost would tell the operator
 * a 545-series sweep was as cheap as a 50-series one.
 */
export function mergeRangeResponses(
  parts: readonly RangeResponse[],
  order: readonly number[],
): RangeResponse {
  const first = parts[0];
  if (!first) throw new Error('mergeRangeResponses: no parts');
  const rank = new Map(order.map((id, i) => [id, i]));
  return {
    bucket: first.bucket,
    agg: first.agg,
    from: first.from,
    to: first.to,
    source: first.source,
    cost: {
      // Buckets is per-request and identical across batches (same window, same bucket).
      buckets: first.cost.buckets,
      series: parts.reduce((n, p) => n + p.cost.series, 0),
      estimatedRawRows: parts.reduce((n, p) => n + p.cost.estimatedRawRows, 0),
    },
    // Requested order, so series colour and CSV column order do not shuffle when batches
    // come back out of order.
    series: parts
      .flatMap((p) => p.series)
      .sort((a, b) => (rank.get(a.seriesId) ?? 0) - (rank.get(b.seriesId) ?? 0)),
  };
}

function sleep(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms));
}

/** Bounded-concurrency map that stops pulling work as soon as one item fails fatally. */
async function inPool<T>(
  items: readonly T[],
  limit: number,
  signal: AbortSignal | undefined,
  work: (item: T) => Promise<void>,
): Promise<void> {
  let next = 0;
  let fatal: unknown = null;
  const worker = async (): Promise<void> => {
    for (;;) {
      if (fatal !== null || signal?.aborted) return;
      const i = next++;
      const item = items[i];
      if (item === undefined) return;
      try {
        await work(item);
      } catch (e) {
        // Stop the stampede: without this the other workers keep issuing requests for a
        // run whose result is already being thrown away.
        fatal = e;
        return;
      }
    }
  };
  await Promise.all(Array.from({ length: Math.max(1, Math.min(limit, items.length)) }, worker));
  if (fatal !== null) throw fatal;
}

async function fetchRangeBatch(
  ids: readonly number[],
  rest: { from: string; to: string; bucket: RangeBucket; agg: Aggregation },
  signal: AbortSignal | undefined,
): Promise<RangeResponse> {
  const query = { seriesIds: ids.join(','), ...rest };
  let last: unknown = null;
  for (let attempt = 0; attempt < BATCH_ATTEMPTS; attempt++) {
    try {
      return await api.get<RangeResponse>('/telemetry/range', query, signal);
    } catch (e) {
      last = e;
      // A 4xx means the request itself is wrong and every input is identical on a retry.
      // For the cost guard specifically it also means this mirror has drifted from the
      // server, which must surface immediately rather than after a backoff.
      if (e instanceof ApiError && e.isClientFault) throw e;
      if (signal?.aborted) throw e;
      if (attempt < BATCH_ATTEMPTS - 1) await sleep(BATCH_RETRY_MS);
    }
  }
  throw last;
}

/**
 * One range query over ARBITRARILY MANY series, by splitting it into requests the cost
 * guard accepts and merging the answers.
 *
 * WHY THIS EXISTS: `/telemetry/range` refuses a request whose `seriesIds` exceed 50 or
 * whose window x series exceeds 250,000 raw rows. Both limits are per REQUEST, not per
 * question — so "chart everything this site polls" (545 series at JAZZMSC5) is not an
 * expensive query, it is 11 cheap ones at 24 h and 33 at 30 d. The old UI answered it by
 * capping the selection at 50 and telling the operator the other 495 were unavailable.
 *
 * The batch size comes from `maxSeriesPerRequest`, which is the server's own arithmetic,
 * so a batch is never rejected. If one ever is, `fetchRangeBatch` throws it straight
 * through instead of retrying — a cost 422 here is a drift bug in the mirror above, and
 * hiding it behind a retry would make it undiagnosable.
 *
 * Failures are NOT all-or-nothing. A 5xx or a dropped connection on one batch costs that
 * batch's series and is reported in `failedSeriesIds`; only a 4xx or a total wipeout
 * rejects the query.
 */
export function useChunkedRange(
  params: {
    seriesIds: number[];
    from: string;
    to: string;
    bucket: RangeBucket;
    agg: Aggregation;
  },
  opts: {
    enabled: boolean;
    refetchMs: number | false;
    focusRefetch: boolean;
    staleMs: number;
    concurrency?: number | undefined;
  },
): { query: UseQueryResult<ChunkedRangeResult, Error>; progress: ChunkedRangeProgress | null } {
  const [progress, setProgress] = useState<ChunkedRangeProgress | null>(null);

  const windowSeconds = (Date.parse(params.to) - Date.parse(params.from)) / 1000;
  const batchSize = maxSeriesPerRequest(windowSeconds, params.bucket);
  const ids = params.seriesIds;
  const rest = { from: params.from, to: params.to, bucket: params.bucket, agg: params.agg };
  const concurrency = opts.concurrency ?? RANGE_CONCURRENCY;

  /*
   * The key is the WHOLE selection, not one per batch.
   *
   * Deliberate: `seriesIds` is what identifies the question, the merged result is what the
   * chart consumes, and one cache entry means one `isFetching`, one `dataUpdatedAt` and one
   * refetch timer. Caching batches individually would let a one-series change reuse most
   * of them, but batch boundaries shift when the selection changes, so the reuse is mostly
   * illusory and the bookkeeping is not.
   */
  const query = useQuery<ChunkedRangeResult, Error>({
    queryKey: keys.range({ ...rest, seriesIds: ids.join(','), chunked: true }),
    queryFn: async ({ signal }) => {
      const batches = chunkIds(ids, batchSize);
      let requestsDone = 0;
      let seriesDone = 0;
      setProgress({
        requestsDone: 0,
        requestsTotal: batches.length,
        seriesDone: 0,
        seriesTotal: ids.length,
      });

      const parts: RangeResponse[] = [];
      const failedSeriesIds: number[] = [];

      await inPool(batches, concurrency, signal, async (batch) => {
        try {
          parts.push(await fetchRangeBatch(batch, rest, signal));
        } catch (e) {
          if (e instanceof ApiError && e.isClientFault) throw e;
          if (signal.aborted) throw e;
          failedSeriesIds.push(...batch);
        } finally {
          requestsDone += 1;
          seriesDone += batch.length;
          setProgress({
            requestsDone,
            requestsTotal: batches.length,
            seriesDone,
            seriesTotal: ids.length,
          });
        }
      });

      // Every batch failed: there is nothing to plot, so this is an error, not a partial.
      if (parts.length === 0) throw new Error('No batch of the range query succeeded');

      return {
        response: mergeRangeResponses(parts, ids),
        requestsOk: parts.length,
        requestsTotal: batches.length,
        failedSeriesIds,
        batchSize,
        // A copy, not the caller's array: `ids` is the caller's `seriesIds` memo, which is
        // replaced on the next selection change, and a result must keep naming the question
        // it actually answered.
        requestedSeriesIds: [...ids],
      };
    },
    // Retries live per BATCH (see `fetchRangeBatch`). Retrying at this level would re-issue
    // every request in the run to recover one of them.
    retry: false,
    // Keep the previous plot and y-domain through a refetch: dropping to a skeleton every
    // 60 s reads as the chart reloading rather than as new data arriving.
    placeholderData: (prev) => prev,
    enabled: opts.enabled && ids.length > 0,
    // `exactOptionalPropertyTypes`: omit the key entirely rather than assign it `undefined`.
    ...(opts.refetchMs ? { refetchInterval: pollInterval(opts.refetchMs) } : {}),
    refetchOnWindowFocus: opts.focusRefetch,
    staleTime: opts.staleMs,
  });

  return { query, progress };
}

/* ────────────────────────────────────────────── the alarm operator axis ── */

/**
 * One alarm's workflow state, timeline, and legal next actions.
 *
 * Not polled on `LIVE_REFETCH_MS` like the alarm list. The list is live because the DEVICE
 * changes it; this changes only when a person acts, and every one of those actions returns
 * the fresh view — so a poll would refetch a value we were just handed.
 */
export function useAlarmTriage(alarmId: string | null) {
  return useQuery({
    ...base,
    queryKey: keys.alarmTriage(alarmId ?? ''),
    queryFn: () => api.get<AlarmTriage>(`/alarms/${alarmId}/triage`),
    enabled: alarmId !== null,
  });
}

/** One person an alarm may be given to — `GET /alarms/assignees`. */
export interface AlarmAssignee {
  id: string;
  name: string;
  /** Invited and never signed in: assignable, and the picker says so. */
  pending: boolean;
}

/**
 * WHO THESE ALARMS MAY BE GIVEN TO — the server's answer, never the header organisation's
 * roster (Hamzah, 2026-10-09). The pickers listed `GET /users`, the organisation selected in the
 * header: with Thunder Energy selected, a K-Solar alarm offered Thunder's people and refused
 * K-Solar's. The server computes this list with the same function the assign refuses with, so
 * what is offered is exactly what will be accepted. Several ids for a flood: the people who may
 * be given every one of them.
 */
export function useAlarmAssignees(alarmIds: readonly string[], enabled = true) {
  const ids = [...alarmIds].sort().join(',');
  return useQuery({
    ...base,
    queryKey: ['alarms', 'assignees', ids] as const,
    queryFn: () => api.get<{ data: AlarmAssignee[] }>('/alarms/assignees', { ids }),
    enabled: enabled && ids !== '',
    staleTime: 60_000,
  });
}

/**
 * Apply a lifecycle action, add a note, or ask the platform to verify.
 *
 * Every one of these returns the whole updated `AlarmTriage`, so the cache is SEEDED from the
 * response rather than invalidated and refetched. That removes the flicker where the drawer
 * shows the old state for a beat after you press a button — and it means the timeline entry
 * the server just wrote is on screen the moment it exists.
 */
export function useAlarmAction(alarmId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      action: AlarmLifecycleAction;
      assigneeId?: string | null;
      note?: string | null;
    }) => api.post<AlarmTriage>(`/alarms/${alarmId}/actions`, body),
    onSuccess: (data) => {
      qc.setQueryData(keys.alarmTriage(alarmId), data);
      /* The list carries the state chip, so it has to hear about this too. */
      void qc.invalidateQueries({ queryKey: ['alarms'], exact: false });
    },
  });
}

export function useAlarmNote(alarmId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (note: string) => api.post<AlarmTriage>(`/alarms/${alarmId}/notes`, { note }),
    onSuccess: (data) => qc.setQueryData(keys.alarmTriage(alarmId), data),
  });
}

export function useAlarmVerify(alarmId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<AlarmTriage>(`/alarms/${alarmId}/verify`, {}),
    onSuccess: (data) => {
      qc.setQueryData(keys.alarmTriage(alarmId), data);
      void qc.invalidateQueries({ queryKey: ['alarms'], exact: false });
    },
  });
}

/**
 * Snooze one alarm (migration 0074) and wake it early. Both return the whole triage view, so
 * the drawer or detail page is seeded from the response, and both invalidate the board — a
 * snoozed row leaves "Needs action" and joins the collapsed "Snoozed" section.
 */
export function useAlarmSnooze(alarmId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { hours: SnoozeHours; reason: string }) =>
      api.post<AlarmTriage>(`/alarms/${alarmId}/snooze`, body),
    onSuccess: (data) => {
      qc.setQueryData(keys.alarmTriage(alarmId), data);
      void qc.invalidateQueries({ queryKey: ['alarms'], exact: false });
    },
  });
}

export function useAlarmUnsnooze(alarmId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.del<AlarmTriage>(`/alarms/${alarmId}/snooze`),
    onSuccess: (data) => {
      qc.setQueryData(keys.alarmTriage(alarmId), data);
      void qc.invalidateQueries({ queryKey: ['alarms'], exact: false });
    },
  });
}
